/**
 * Generic MDS (Message Delivery Service) client for real-time WebSocket communication.
 *
 * This client is agnostic to specific features - it provides a generic WebSocket
 * connection that can route messages to registered handlers based on message type.
 */

import { MDS } from '@fanduel/casino-swj-consumer';
import type { MdsInstance, MdsRawMessage, MessageHandler } from './types';

type MdsClientInstance = {
    on: (messageType: string, handler: MessageHandler) => void;
    off: (messageType: string, handler: MessageHandler) => void;
    initSocket: (userId: string) => void;
    closeConnection: () => void;
};

let _instance: MdsClientInstance | undefined;
let _messageHandlers: Map<string, MessageHandler[]> | undefined;

/**
 * Internal message handler that routes messages to registered handlers.
 */
const handleMessage = (rawMessage: MdsRawMessage): void => {
    const message = rawMessage.data?.addedMessage;
    if (!message?.type || !_messageHandlers) {
        return;
    }

    const handlers = _messageHandlers.get(message.type);
    if (handlers) {
        handlers.forEach((handler) => handler(message));
    }
};

/**
 * Gets or creates the singleton instance of MdsClient.
 *
 * @param endpoint - The MDS WebSocket endpoint URL
 * @returns The MdsClient singleton instance
 */
const getInstance = (endpoint: string): MdsClientInstance => {
    if (!_instance) {
        const mds = MDS(endpoint, () => {}, handleMessage) as MdsInstance;
        _messageHandlers = new Map<string, MessageHandler[]>();

        _instance = {
            on: (messageType: string, handler: MessageHandler) => {
                const handlers = _messageHandlers!.get(messageType) || [];
                handlers.push(handler);
                _messageHandlers!.set(messageType, handlers);
            },
            off: (messageType: string, handler: MessageHandler) => {
                const handlers = _messageHandlers!.get(messageType);
                if (handlers) {
                    const index = handlers.indexOf(handler);
                    if (index > -1) {
                        handlers.splice(index, 1);
                    }
                }
            },
            initSocket: (userId: string) => {
                mds.initSocket(userId);
            },
            closeConnection: () => {
                mds.closeConnection();
            }
        };
    }
    return _instance;
};

/**
 * Resets the singleton instance. Useful for testing or when user logs out.
 */
const resetInstance = (): void => {
    if (_instance) {
        _instance.closeConnection();
        _messageHandlers?.clear();
        _instance = undefined;
        _messageHandlers = undefined;
    }
};

export const MdsClient = {
    getInstance,
    resetInstance
};
